import { MaterialCommunityIcons } from "@expo/vector-icons";
import { LinearGradient } from "expo-linear-gradient";
import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { Platform, StyleSheet, TouchableOpacity, View } from "react-native";
import { Text, TextInput } from "react-native-paper";

import { Chapter } from "./Chapter";

import { RecordingStripe } from "@/components/recorder/RecordingStripe";
import { RingHalo } from "@/components/recorder/RingHalo";
import { ThinkingStripe } from "@/components/recorder/ThinkingStripe";
import { useQuestionRecorder } from "@/hooks/useQuestionRecorder";
import { useResolvedReducedMotion } from "@/hooks/useResolvedReducedMotion";
import { colors, fonts, gradients, radius } from "@/lib/theme";

interface Props {
    visitId: string;
    sectionId: string;
    /** Server-canonical narrative text from getSectionState. */
    initialText: string;
    /** Optional pre-stored guidance text from the form definition. */
    guidanceText: string | null;
    /** Called by the orchestrator's debounce-save engine; fires onChange of text. */
    onChange: (text: string) => void;
    readOnly: boolean;
    /** Bumped by the orchestrator after scrolling here from a validation error card. */
    highlightTick: number;
}

export interface NarrativeChapterHandle {
    flushSave: () => string;
    getText: () => string;
}

/**
 * Chapter 02 — section narrative. Same in-card UI as a TextQuestion: textarea
 * + 48px brand-gradient mic at the bottom-right. Tap the mic → RecordingStripe
 * with the 22-bar LiveWaveform. Stop → ThinkingStripe with the orb +
 * "Transcribing" + dots. Transcript appends to the textarea.
 *
 * The only difference from TextQuestion is the recorder mode — narrative
 * voice writes through getAudioUploadUrl + transcribeRecording (section
 * scope), not the per-question audio endpoints. useQuestionRecorder takes
 * a `mode: "section"` flag for that.
 */
export const NarrativeChapter = forwardRef<NarrativeChapterHandle, Props>(function NarrativeChapter(
    { visitId, sectionId, initialText, guidanceText, onChange, readOnly, highlightTick },
    ref
) {
    const [text, setText] = useState(initialText);
    const [glow, setGlow] = useState(false);

    useEffect(() => {
        setText(initialText);
    }, [initialText]);

    useEffect(() => {
        if (highlightTick > 0) {
            setGlow(true);
            const t = setTimeout(() => setGlow(false), 1400);
            return () => clearTimeout(t);
        }
        return undefined;
    }, [highlightTick]);

    useImperativeHandle(
        ref,
        () => ({
            flushSave: () => text,
            getText: () => text
        }),
        [text]
    );

    const recorder = useQuestionRecorder({
        visitId,
        sectionId,
        mode: "section",
        onTranscribed: (transcript) => {
            const next = (text ?? "").trim();
            const combined = next.length === 0 ? transcript : `${next} ${transcript}`;
            setText(combined);
            onChange(combined);
        }
    });

    const phase = recorder.state.status;
    const isRecording = phase === "recording";
    const isBusy = phase === "recording" || phase === "thinking";

    const handleChange = (v: string) => {
        setText(v);
        onChange(v);
    };

    return (
        <Chapter num="02" title="Narrative" helper="In your own words.">
            <View style={styles.column}>
                {guidanceText ? (
                    <View style={styles.guidanceCard}>
                        <Text style={styles.guidanceLabel}>Narrative Guidance</Text>
                        <Text style={styles.guidanceText}>{guidanceText}</Text>
                    </View>
                ) : null}

                <View
                    style={[
                        styles.card,
                        isBusy
                            ? { borderColor: "rgba(69,210,176,0.5)", borderWidth: 1.5 }
                            : { borderColor: colors.surfaceBorder, borderWidth: 1 },
                        glow
                            ? {
                                  shadowColor: colors.teal400,
                                  shadowOpacity: 0.3,
                                  shadowRadius: 12,
                                  shadowOffset: { width: 0, height: 0 }
                              }
                            : null
                    ]}
                >
                    <TextInput
                        mode="outlined"
                        value={text}
                        onChangeText={handleChange}
                        disabled={readOnly || isBusy}
                        multiline
                        numberOfLines={6}
                        placeholder="Document the patient's status in your own words…"
                        style={[styles.textarea, isBusy && styles.textareaDimmed]}
                        outlineStyle={styles.textareaOutline}
                    />

                    <View style={styles.footer}>
                        {isRecording ? (
                            <RecordingStripe
                                seconds={recorder.seconds}
                                meter={recorder.meter}
                                reduced={false}
                                onCancel={recorder.cancel}
                                onStop={() => {
                                    void recorder.stop();
                                }}
                            />
                        ) : recorder.state.status === "thinking" ? (
                            <ThinkingStripe
                                fieldLabel={recorder.state.field.label}
                                reduced={false}
                            />
                        ) : (
                            <>
                                <Text style={styles.wordCount}>
                                    {text.split(/\s+/).filter(Boolean).length} words · saved
                                </Text>
                                <InCardMic
                                    disabled={readOnly || !visitId || !sectionId}
                                    onPress={() => {
                                        void recorder.start({
                                            id: `narrative-${sectionId}`,
                                            label: "Narrative"
                                        });
                                    }}
                                />
                            </>
                        )}
                    </View>
                </View>
            </View>
        </Chapter>
    );
});

function InCardMic({ disabled, onPress }: { disabled: boolean; onPress: () => void }) {
    const motion = useResolvedReducedMotion();
    return (
        <TouchableOpacity
            onPress={onPress}
            disabled={disabled}
            activeOpacity={0.85}
            accessibilityRole="button"
            accessibilityLabel="Record voice note"
            style={styles.micHit}
        >
            <View style={styles.micHost}>
                {motion.resolved ? <RingHalo reduced={motion.reduced} /> : null}
                <LinearGradient
                    colors={[...gradients.brand]}
                    start={{ x: 0, y: 0 }}
                    end={{ x: 1, y: 1 }}
                    style={[styles.micCircle, disabled && styles.micCircleDisabled]}
                >
                    <MaterialCommunityIcons name="microphone" size={22} color="#fff" />
                </LinearGradient>
            </View>
        </TouchableOpacity>
    );
}

const styles = StyleSheet.create({
    column: { gap: 14 },
    guidanceCard: {
        backgroundColor: "#F3F8FF",
        borderColor: "#D4E4F7",
        borderWidth: 1,
        borderRadius: radius.md,
        padding: 14,
        gap: 6
    },
    guidanceLabel: {
        fontFamily: fonts.extraBold,
        fontSize: 11,
        color: "#1565C0",
        letterSpacing: 0.4,
        textTransform: "uppercase"
    },
    guidanceText: {
        fontFamily: fonts.medium,
        fontSize: 14,
        color: colors.ink,
        lineHeight: 20
    },
    card: {
        backgroundColor: "#fff",
        borderRadius: 18,
        padding: 16,
        gap: 12
    },
    textarea: {
        backgroundColor: colors.gray100,
        minHeight: 150,
        maxHeight: 240,
        fontSize: 14.5
    },
    textareaDimmed: {
        opacity: 0.35
    },
    textareaOutline: {
        borderRadius: radius.md,
        borderWidth: 1.5,
        borderColor: colors.border
    },
    footer: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
        minHeight: 48
    },
    wordCount: {
        flexShrink: 0,
        fontFamily: fonts.extraBold,
        fontSize: 10.5,
        color: colors.textTertiary,
        letterSpacing: 0.6,
        textTransform: "uppercase"
    },
    micHit: {
        width: 48,
        height: 48,
        alignItems: "center",
        justifyContent: "center",
        overflow: "visible"
    },
    micHost: {
        width: 48,
        height: 48,
        overflow: "visible",
        alignItems: "center",
        justifyContent: "center"
    },
    micCircle: {
        width: 48,
        height: 48,
        borderRadius: 24,
        alignItems: "center",
        justifyContent: "center",
        ...Platform.select({
            ios: {
                shadowColor: "#E748F5",
                shadowOffset: { width: 0, height: 8 },
                shadowOpacity: 0.3,
                shadowRadius: 14
            },
            android: { elevation: 6 },
            default: {}
        })
    },
    micCircleDisabled: {
        opacity: 0.5
    }
});
